import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { upsellTransition, UpsellTransitionStages } from '@ecs-uk/web-common-mobile-view-helpers';

import fetchAction from '../../actions/fetch';
import fulfillSubscription from '../../actions/fulfillments/fulfill';
import { PaymentContext } from '../PaymentOrchestrator/PaymentContextProvider';
import paymentCompleteTransition from '../PaymentOrchestrator/transitions/paymentCompleteTransition';
import { trackSwitchFulfillmentAttempt, trackSwitchFulfillmentStarted } from '../../helpers/switch/analytics';
import useTrackUpsellStage from '../PaymentOrchestrator/hooks/useTrackUpsellStage';
import pollEntitlementsUntilComplete from '../../helpers/pollEntitlementsUntilComplete';
import useEntitlementsPolling from '../../hooks/flexHooks/useEntitlementsPolling';
import isFlexUser from '../../helpers/isFlexUser';
import { PAYMENT_LOG_STEPS } from '../../constants/paymentLogSteps';
import ProcessingSpinner from './ProcessingSpinner';
import { channels, splitsList } from '../../constants';
import { FULFILLMENT_IN_PROGRESS_KEY } from '../PaymentOrchestrator/constants/fulfillmentGuard';
import useFlexData from '../../hooks/flexHooks/useFlexData';
import NewLoading from '../../components/CommonComponents/NewLoading';

const FULFILLMENT_UNKNOWN_STATUS = 'FULFILLMENT_UNKNOWN';

const PaymentProcessing = () => {
  const paymentContext = useContext(PaymentContext);
  const { resolvedUpgradeOffer, resolvedCurrentSubscription, isFlexFlow, isWebview, tokens } = paymentContext;
  const isFlexMigrationFailed = tokens?.flexUserMigrationFailed === true;

  const upsellChannel = isWebview ? channels.app : channels.online;
  const fulfillmentStatus = useSelector(state => state.fulfillments.success ?? FULFILLMENT_UNKNOWN_STATUS);
  const reduxCurrentSubscription = useSelector(state => state.subscriptions.current);
  const currentSubscription = isFlexFlow ? resolvedCurrentSubscription : reduxCurrentSubscription;
  const productFamilyDuringPurchase = currentSubscription?.family;
  const currentPlanCode = currentSubscription?.planCode;
  const isFlexSplitEnabled = useSelector(state => isFlexUser(state.split));
  const hasCurrentSubscriptionPlanCode = Boolean(currentPlanCode);
  const isFlexJourney = isFlexFlow || isFlexSplitEnabled || hasCurrentSubscriptionPlanCode;
  const shouldCallFulfillmentsApi = !isFlexJourney || isFlexMigrationFailed;

  const isGuardEnabled = useSelector(state => state.split?.treatments?.[splitsList.preventDuplicatePayment]);

  const { isNewDesignEnabled, isFlexUser: flexUser } = useFlexData();
  const showNewLoading = isNewDesignEnabled && (flexUser || isFlexSplitEnabled);

  const trackUpsellStage = useTrackUpsellStage();
  const dispatch = useDispatch();
  const [flexFulfillmentStatus, setFlexFulfillmentStatus] = React.useState(FULFILLMENT_UNKNOWN_STATUS);
  const { startPolling, pollingComplete } = useEntitlementsPolling(currentPlanCode);
  const effectiveFulfillmentStatus = shouldCallFulfillmentsApi ? fulfillmentStatus : flexFulfillmentStatus;

  // RTK polling completion handler (only active when isFlexFlow is explicitly set)
  useEffect(() => {
    if (pollingComplete) {
      setFlexFulfillmentStatus(true);
    }
  }, [pollingComplete]);

  useEffect(() => {
    if (isGuardEnabled) {
      sessionStorage.setItem(FULFILLMENT_IN_PROGRESS_KEY, Date.now().toString());
    }
    if (!isFlexFlow) {
      trackSwitchFulfillmentStarted();
    }
  }, [isGuardEnabled, isFlexFlow]);

  useEffect(() => {
    trackUpsellStage(PAYMENT_LOG_STEPS.SUBSCRIPTION_FULFILLMENT_STARTED);
  }, [trackUpsellStage]);

  // Start fulfillment for non-flex users, or poll entitlements for flex users.
  useEffect(() => {
    if (shouldCallFulfillmentsApi) {
      dispatch(
        fulfillSubscription(resolvedUpgradeOffer.id, upsellChannel, undefined, false, {
          isFlexMigrationFailed,
          ...(isFlexFlow ? { isFlexCustomer: true, planCode: resolvedUpgradeOffer.planCode } : {}),
        })
      );
      return;
    }

    // Explicit flex flow uses RTK Query polling; implicit flex uses legacy fetch polling.
    if (isFlexFlow) {
      startPolling();
      return;
    }

    let cancelled = false;
    let hasTrackedPollRetry = false;

    const pollEntitlements = async () => {
      const completed = await pollEntitlementsUntilComplete({
        dispatch,
        fetchAction,
        initialDelayMs: 5000,
        currentPlanCode,
        shouldStop: () => cancelled,
        onRetryError: () => {
          // Record only the first transient poll error to avoid noisy duplicate events.
          if (!hasTrackedPollRetry) {
            hasTrackedPollRetry = true;
            trackUpsellStage(PAYMENT_LOG_STEPS.SUBSCRIPTION_FULFILLMENT_POLL_RETRY);
          }
        },
        onTimeout: () => {
          trackUpsellStage(PAYMENT_LOG_STEPS.SUBSCRIPTION_FULFILLMENT_POLL_TIMEOUT);
        },
      });

      if (!cancelled) {
        // Treat timeout as success so users can proceed while backend fulfillment finalizes asynchronously.
        setFlexFulfillmentStatus(completed || true);
      }
    };

    pollEntitlements();

    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    shouldCallFulfillmentsApi,
    isFlexFlow,
    startPolling,
    trackUpsellStage,
    resolvedUpgradeOffer.id,
    upsellChannel,
    isFlexMigrationFailed,
  ]);

  // listen for a fulfillment result and trigger the next transition (thanks or error/reset)
  useEffect(() => {
    if (effectiveFulfillmentStatus !== FULFILLMENT_UNKNOWN_STATUS) {
      const fulfillmentsSuccessful = effectiveFulfillmentStatus === true;

      if (!isFlexFlow) {
        trackSwitchFulfillmentAttempt(fulfillmentsSuccessful);
      }
      trackUpsellStage(
        fulfillmentsSuccessful
          ? PAYMENT_LOG_STEPS.SUBSCRIPTION_FULFILLED
          : PAYMENT_LOG_STEPS.SUBSCRIPTION_FULFILLMENT_FAILED
      );

      if (isGuardEnabled) {
        sessionStorage.removeItem(FULFILLMENT_IN_PROGRESS_KEY);
      }
      upsellTransition({ stage: UpsellTransitionStages.UNLOCK });
      dispatch(paymentCompleteTransition(paymentContext, effectiveFulfillmentStatus, productFamilyDuringPurchase));
    }
  }, [
    dispatch,
    effectiveFulfillmentStatus,
    isFlexFlow,
    isGuardEnabled,
    paymentContext,
    productFamilyDuringPurchase,
    trackUpsellStage,
  ]);

  return showNewLoading ? <NewLoading isWebview={isWebview} /> : <ProcessingSpinner isWebview={isWebview} enableText />;
};

export default PaymentProcessing;
